<template>
  <section class="min-h-screen flex items-center justify-center bg-bg text-text px-4 py-12 relative overflow-hidden">
    <!-- subtle grid backdrop -->
    <div
      class="absolute inset-0 opacity-[0.04] pointer-events-none"
      style="background-image: linear-gradient(var(--line) 1px, transparent 1px), linear-gradient(90deg, var(--line) 1px, transparent 1px); background-size: 48px 48px;"
    ></div>
    <!-- lime glow -->
    <div class="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full pointer-events-none"
         style="background: radial-gradient(closest-side, rgba(184,230,0,0.08), transparent);"></div>

    <div class="w-full max-w-sm relative z-10">
      <!-- brand mark -->
      <div class="flex items-center justify-center gap-3 mb-8">
        <div class="w-10 h-10 rounded-[6px] bg-lime text-black font-mono font-bold text-lg flex items-center justify-center">
          N
        </div>
        <div>
          <div class="text-[15px] font-semibold text-text leading-tight">Neutral Fuels</div>
          <div class="font-mono text-[10.5px] uppercase tracking-[0.16em] text-text-3 leading-tight">Admin Portal</div>
        </div>
      </div>

      <div class="bg-bg-1 border border-line rounded-[var(--radius-lg)] p-6">
        <h1 class="text-[18px] font-semibold text-text mb-1">Sign in</h1>
        <p class="text-[13px] text-text-3 mb-6">Restricted to authorised admins.</p>

        <form @submit.prevent="login" class="space-y-4">
          <div>
            <label for="email" class="block font-mono text-[10.5px] uppercase tracking-[0.12em] text-text-3 mb-1.5">Email</label>
            <UiInput
              id="email"
              v-model="email"
              type="email"
              placeholder="you@neutralfuels.com"
              :left-icon="Mail"
              autofocus
            />
          </div>

          <div>
            <label for="password" class="block font-mono text-[10.5px] uppercase tracking-[0.12em] text-text-3 mb-1.5">Password</label>
            <div class="relative">
              <Lock :size="15" class="absolute left-3 top-1/2 -translate-y-1/2 text-text-3 pointer-events-none" />
              <input
                id="password"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                placeholder="••••••••"
                class="w-full bg-bg-1 border border-line-2 rounded-[var(--radius-md)]
                       text-sm text-text placeholder:text-text-4
                       focus:outline-none focus:border-lime/60 focus:ring-1 focus:ring-lime/30
                       transition-colors pl-9 pr-10 py-2"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-3 hover:text-text p-0.5"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
              >
                <EyeOff v-if="showPassword" :size="14" />
                <Eye v-else :size="14" />
              </button>
            </div>
          </div>

          <div
            v-if="error"
            class="flex items-start gap-2 px-3 py-2 rounded-[var(--radius-md)] border border-[rgba(240,82,82,0.35)] bg-[rgba(240,82,82,0.06)]"
          >
            <AlertCircle :size="14" class="text-err shrink-0 mt-0.5" />
            <p class="text-err text-[12.5px] leading-snug">{{ error }}</p>
          </div>

          <UiButton
            type="submit"
            variant="primary"
            size="lg"
            :loading="isPending"
            class="w-full"
          >
            {{ isPending ? 'Signing in…' : 'Sign in' }}
          </UiButton>
        </form>

        <div class="mt-5 pt-4 border-t border-line flex items-center justify-between">
          <span class="font-mono text-[10.5px] text-text-4">{{ versionNumber }}</span>
          <router-link
            to="/forgot-password"
            class="text-[12px] text-text-3 hover:text-lime transition-colors"
          >
            Forgot password?
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { Mail, Lock, Eye, EyeOff, AlertCircle } from "lucide-vue-next";
import UiInput from "@/components/ui/UiInput.vue";
import UiButton from "@/components/ui/UiButton.vue";

export default {
  components: { UiInput, UiButton, Lock, Eye, EyeOff, AlertCircle },
  setup() {
    const email = ref("");
    const password = ref("");
    const showPassword = ref(false);
    const store = useStore();
    const isPending = ref(false);
    const error = ref(null);

    const login = async () => {
      try {
        error.value = null;
        isPending.value = true;
        await store.dispatch("login", {
          email: email.value,
          password: password.value,
        });
        window.location.reload();
      } catch (err) {
        isPending.value = false;
        error.value = err.message;
        console.log(err.message);
      }
    };

    return {
      email,
      password,
      login,
      showPassword,
      isPending,
      error,
      versionNumber: computed(() => store.state.versionNumber),
      Mail, Lock, Eye, EyeOff, AlertCircle,
    };
  },
};
</script>
